(function () {
  const WIDGET_SELECTOR = '[id^="appstle_subscription_widget"]';
  const PLAN_SELECTOR = ".appstle_input_wrapper";
  const DETAILS_SELECTOR = ".appstle-frequency-description";
  const ACTIVE_CLASS = "appstle-active-option";

  const REWARD_SELECTOR = ".appstle-free-product-loyalty";

  const PLAN_CONFIG = {};

  const productCache = new Map();

  /* =========================================================
     HELPERS
  ========================================================= */

  function normalize(value) {
    return (value || "")
      .toLowerCase()
      .replace(/[®™]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function getPlanMonthsFromValue(value) {
    const text = normalize(value);

    const match = text.match(/(\d+)\s*[- ]?\s*months?/i);

    return match ? Number(match[1]) : null;
  }

  function getAppstlePlanConfig(months) {
    const plans = window.AppstleSubscription?.Config?.sellingPlansJson || [];

    return (
      plans.find((plan) => {
        const planMonths = getPlanMonthsFromValue(
          plan.frequencyName || plan.name || "",
        );

        return planMonths === Number(months);
      }) || null
    );
  }

  function getPlanGiftCount(months) {
    const planConfig = getAppstlePlanConfig(months);

    if (!planConfig) {
      return 0;
    }

    const cycles = Array.isArray(planConfig.appstleCycles)
      ? planConfig.appstleCycles
      : [];

    const gifts = cycles.flatMap((cycle) => {
      return Array.isArray(cycle.freeProducts) ? cycle.freeProducts : [];
    });

    return gifts.length;
  }

  function getPlanGiftDefinitions(months) {
    const planConfig = getAppstlePlanConfig(months);

    if (!planConfig) {
      return [];
    }

    const cycles = Array.isArray(planConfig.appstleCycles)
      ? planConfig.appstleCycles
      : [];

    const products = [];

    cycles.forEach((cycle) => {
      const cycleProducts = Array.isArray(cycle.freeProducts)
        ? cycle.freeProducts
        : [];

      cycleProducts.forEach((product) => {
        products.push({
          ...product,

          discountType: cycle.discountType || "",

          discountValue:
            cycle.discountOffer ??
            cycle.discountPercentage ??
            cycle.discountValue ??
            null,
        });
      });
    });

    const seen = new Set();

    return products.filter((gift) => {
      const key =
        gift.freeVariantId || gift.freeProductHandle || gift.freeProductTitle;

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);

      return true;
    });
  }

  function getPlanDiscount(plan) {
    const nativeDiscount = plan.querySelector(
      ".appstle_sellingPlan_discountText",
    );

    if (!nativeDiscount) {
      return "";
    }

    return (nativeDiscount.textContent || "").replace(/\s+/g, " ").trim();
  }

  function moneyNumber(value) {
    const number = Number(
      String(value || "")
        .replace(/,/g, "")
        .replace(/[^\d.]/g, ""),
    );

    return Number.isFinite(number) ? number : null;
  }

  function formatMoneyFromCents(cents) {
    const amount = Number(cents);

    if (!Number.isFinite(amount)) {
      return "";
    }

    const currency =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.activeCurrency ||
      "USD";

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch (error) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  }

  function getRewardVariantHint(reward) {
    const rawTitle = reward?.rawTitle || "";

    const parts = rawTitle.split(/\s+-\s+/);

    if (parts.length < 2) {
      return "";
    }

    return normalize(parts.slice(1).join(" - "));
  }

  function getPlanType(plan) {
    const clone = plan.cloneNode(true);

    clone
      .querySelectorAll(".as-appstle-plan-head, .as-appstle-extra")
      .forEach((element) => element.remove());

    const text = normalize(clone.textContent);

    if (
      text.includes("one-time") ||
      text.includes("one time") ||
      text.includes("no subscription")
    ) {
      return {
        type: "one-time",
        months: null,
      };
    }

    const months = getPlanMonthsFromValue(text);

    if (months) {
      return {
        type: "subscription",
        months,
      };
    }

    return null;
  }

  /* =========================================================
     CURRENT VARIANT
  ========================================================= */

  function getCurrentVariantId() {
    const productForm =
      document.querySelector('product-form form[action*="/cart/add"]') ||
      document.querySelector('.product-form form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    const variantInput = productForm?.querySelector('[name="id"]');

    return variantInput?.value ? String(variantInput.value) : "";
  }
  function cleanPackValue(value) {
    const text = String(value || "").trim();

    const match = text.match(/(\d+)/);

    return match ? Number(match[1]) : null;
  }

  function cleanSizeValue(value) {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function getVariantOptionMap(product, variant) {
    const result = {};

    const productOptions = Array.isArray(product?.options)
      ? product.options
      : [];

    const variantOptions = Array.isArray(variant?.options)
      ? variant.options
      : [];

    productOptions.forEach((option, index) => {
      const optionName =
        typeof option === "string" ? option : option?.name || "";

      const optionValue = variantOptions[index];

      if (!optionName || optionValue == null) {
        return;
      }

      result[normalize(optionName)] = String(optionValue).trim();
    });

    return result;
  }

  function getVariantCardTitle(product, variant) {
    const options = getVariantOptionMap(product, variant);

    /*
     * Підтримуємо різні можливі назви option.
     */
    const packValue =
      options.pack || options.quantity || options["pack size"] || "";

    const sizeValue =
      options.size || options.volume || options["size / volume"] || "";

    const packCount = cleanPackValue(packValue);
    const size = cleanSizeValue(sizeValue);

    /*
     * Назва самого продукту.
     *
     * "Subscription" прибираємо, бо це назва картки товару,
     * а не назва subscription plan.
     */
    const productTitle = String(product?.title || "")
      .replace(/\bsubscription\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();

    /*
     * PACK + SIZE
     * 2 Pack + 4 fl oz
     * => 2 × 4 fl oz Castor Oil
     */
    if (packCount && size) {
      return `${packCount} × ${size} ${productTitle}`;
    }

    /*
     * ONLY SIZE
     * 16 fl oz
     * => 16 fl oz Castor Oil
     */
    if (size) {
      return `${size} ${productTitle}`;
    }

    /*
     * ONLY PACK
     * 2 Pack
     * => 2 × Castor Oil
     */
    if (packCount) {
      return `${packCount} × ${productTitle}`;
    }

    /*
     * NO PACK / NO SIZE
     */
    return productTitle;
  }

  async function getCurrentVariantProduct(plan) {
    const variantId = getCurrentVariantId();

    if (!variantId) {
      return null;
    }

    const productHandle =
      window.ShopifyAnalytics?.meta?.product?.handle ||
      window.meta?.product?.handle ||
      "";

    let productUrl = "";

    if (productHandle) {
      productUrl = `/products/${productHandle}`;
    } else {
      productUrl = window.location.pathname.split("?")[0];
    }

    try {
      const response = await fetch(`${productUrl}.js`, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        return null;
      }

      const product = await response.json();

      const variants = product?.variants || [];

      const variant =
        variants.find((item) => String(item.id) === variantId) ||
        variants[0] ||
        null;

      if (!variant) {
        return null;
      }

      const image =
        variant?.featured_image?.src ||
        variant?.featured_image?.url ||
        product?.featured_image ||
        "";

      /*
       * Кількість пляшок у вибраному PACK.
       * Якщо PACK немає — повернеться 1.
       */
      const packCount = getSelectedPackCount();

      /*
       * Shopify variant price = total price поточного pack variant.
       *
       * Наприклад:
       * 2 PACK variant = $79.98
       *
       * Значить regular price за bottle:
       * $79.98 / 2 = $39.99
       */
      const variantPriceCents =
        variant?.price != null ? Number(variant.price) : null;

      const regularPerBottleCents =
        Number.isFinite(variantPriceCents) && packCount > 0
          ? variantPriceCents / packCount
          : null;

      const regularPerBottle =
        regularPerBottleCents != null
          ? formatMoneyFromCents(regularPerBottleCents)
          : "";

      /*
       * Уже готова subscription price з Appstle.
       *
       * Наприклад:
       * $34.00/bottle
       */
      const subscriptionPriceRaw = plan
        ? findPlanPrice(plan, "subscription")
        : "";

      const subscriptionPrice = subscriptionPriceRaw
        ? subscriptionPriceRaw.replace("/bottle", "")
        : "";

      return {
        title: getVariantCardTitle(product, variant),

        image,

        url: `${productUrl}?variant=${variant.id}`,

        comparePrice: regularPerBottle,

        price: subscriptionPrice,

        gift: false,

        currentProduct: true,
      };
    } catch (error) {
      console.warn("[AS Appstle] Current variant load failed:", error);

      return null;
    }
  }

  function getTitle(planData) {
    if (!planData) {
      return "";
    }

    if (planData.type === "one-time") {
      return "ONE-TIME PURCHASE";
    }

    if (planData.months) {
      return `${planData.months}-MONTH SUPPLY`;
    }

    return "";
  }

  function giftIcon() {
    return `
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 10H20V21H4V10Z"
          stroke="currentColor"
          stroke-width="1.8"
        />

        <path
          d="M2.5 6.5H21.5V10H2.5V6.5Z"
          stroke="currentColor"
          stroke-width="1.8"
        />

        <path
          d="M12 6.5V21"
          stroke="currentColor"
          stroke-width="1.8"
        />

        <path
          d="M12 6.5C9.5 6.5 7 5.7 7 3.8C7 2.7 7.9 2 9 2C11 2 12 4.3 12 6.5Z"
          stroke="currentColor"
          stroke-width="1.8"
        />

        <path
          d="M12 6.5C14.5 6.5 17 5.7 17 3.8C17 2.7 16.1 2 15 2C13 2 12 4.3 12 6.5Z"
          stroke="currentColor"
          stroke-width="1.8"
        />
      </svg>
    `;
  }

  /* =========================================================
     NATIVE PLAN PRICE
  ========================================================= */

  function getSelectedPackCount() {
    const candidates = Array.from(
      document.querySelectorAll(
        '[class*="active"], [class*="selected"], [aria-checked="true"], input:checked',
      ),
    );

    for (const element of candidates) {
      const target = element.closest("label, button, div") || element;

      const text = (target.textContent || "").replace(/\s+/g, " ").trim();

      const match = text.match(/\b(\d+)\s*PACK\b/i);

      if (match) {
        return Number(match[1]);
      }
    }

    const variantInput = document.querySelector(
      'form[action*="/cart/add"] input[name="id"]',
    );

    if (variantInput) {
      const form = variantInput.closest("form");

      const text = form?.textContent || "";

      const match = text.match(/\b(\d+)\s*PACK\b/i);

      if (match) {
        return Number(match[1]);
      }
    }

    /*
     * Якщо PACK option немає,
     * вважаємо що це одна пляшка.
     */
    return 1;
  }

  function findPlanTotalPrice(plan) {
    const nativeRoot =
      plan.querySelector(".appstle-selling-plan-detail-wrapper") ||
      plan.querySelector("label") ||
      plan;

    const clone = nativeRoot.cloneNode(true);

    clone
      .querySelectorAll(
        `${DETAILS_SELECTOR}, .as-appstle-plan-head, .as-appstle-extra`,
      )
      .forEach((element) => element.remove());

    const text = (clone.textContent || "").replace(/\s+/g, " ").trim();

    const matches = [
      ...text.matchAll(
        /([$€£])\s*([\d,]+(?:\.\d{1,2})?)(?:\s*\/\s*(day|bottle))?/gi,
      ),
    ];

    const prices = matches
      .map((match) => ({
        symbol: match[1],

        value: moneyNumber(match[2]),

        unit: (match[3] || "").toLowerCase(),
      }))
      .filter((item) => item.value !== null && !item.unit);

    if (!prices.length) {
      return null;
    }

    return prices.reduce((lowest, current) => {
      if (!lowest) {
        return current;
      }

      return current.value < lowest.value ? current : lowest;
    }, null);
  }

  function findPlanPrice(plan, type) {
    const priceData = findPlanTotalPrice(plan);

    if (!priceData) {
      return "";
    }

    if (type === "one-time") {
      return `${priceData.symbol}${priceData.value.toFixed(2)}`;
    }

    const packCount = getSelectedPackCount();

    const pricePerBottle = priceData.value / packCount;

    return `${priceData.symbol}${pricePerBottle.toFixed(2)}/bottle`;
  }

  /* =========================================================
     APPSTLE REWARDS PARSER
  ========================================================= */

  function cleanGiftProductName(value) {
    return (value || "")
      .replace(/\s+/g, " ")
      .replace(/^[-–—,\s]+/, "")
      .replace(/[-–—,\s]+$/, "")
      .trim();
  }

  function getGiftSearchTitle(rawTitle) {
    return cleanGiftProductName(
      rawTitle
        .replace(/\s+-\s+\d+\s*Pack.*$/i, "")
        .replace(/\s+-\s+\d+\s*(?:oz|fl oz).*$/i, ""),
    );
  }

  function parseRewardElement(element) {
    const text = (element.textContent || "").replace(/\s+/g, " ").trim();

    if (!text) {
      return [];
    }

    let productsText = text
      .replace(/^.*?get\s+(?:a\s+)?(?:\d+\s+)?free\s+gifts?\s*:?\s*/i, "")
      .replace(/^.*?get\s+(?:a\s+)?free\s+/i, "")
      .replace(/\s+with\s+your.*$/i, "")
      .replace(/\s+on\s+your.*$/i, "")
      .replace(/\s+for\s+your.*$/i, "")
      .trim();

    const singleValueMatch = productsText.match(
      /\s+worth\s+of\s+\$?([\d,.]+)/i,
    );

    productsText = productsText
      .replace(/\s+worth\s+of\s+\$?[\d,.]+.*$/i, "")
      .trim();

    let rawProducts = productsText
      .split(/\s*,\s*(?=[A-Z])/)
      .map(cleanGiftProductName)
      .filter(Boolean);

    if (rawProducts.length === 1) {
      const andSplit = productsText
        .split(/\s+(?:and|&)\s+(?=[A-Z])/i)
        .map(cleanGiftProductName)
        .filter(Boolean);

      if (andSplit.length > 1) {
        rawProducts = andSplit;
      }
    }

    return rawProducts.map((rawTitle) => ({
      rawTitle,

      searchTitle: getGiftSearchTitle(rawTitle),

      comparePrice:
        rawProducts.length === 1 && singleValueMatch
          ? `$${singleValueMatch[1]}`
          : "",

      sourceText: text,
    }));
  }

  function getAppstleRewards(plan) {
    const widget = plan.closest(WIDGET_SELECTOR);

    if (!widget) {
      return [];
    }

    const rewards = Array.from(widget.querySelectorAll(REWARD_SELECTOR))
      .flatMap(parseRewardElement)
      .filter(Boolean);

    const seen = new Set();

    return rewards.filter((reward) => {
      const key = normalize(reward.searchTitle || reward.rawTitle);

      if (!key) {
        return false;
      }

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);

      return true;
    });
  }

  /* =========================================================
     SHOPIFY PRODUCT SEARCH
  ========================================================= */

  function getProductScore(product, reward) {
    const productTitle = normalize(product?.title);

    const searchTitle = normalize(reward.searchTitle);

    const rawTitle = normalize(reward.rawTitle);

    if (!productTitle) {
      return 0;
    }

    if (productTitle === searchTitle) {
      return 100;
    }

    if (rawTitle.startsWith(productTitle)) {
      return 90;
    }

    if (searchTitle.includes(productTitle)) {
      return 80;
    }

    if (productTitle.includes(searchTitle)) {
      return 75;
    }

    const searchWords = searchTitle
      .split(" ")
      .filter((word) => word.length > 2);

    const matchedWords = searchWords.filter((word) =>
      productTitle.includes(word),
    ).length;

    return matchedWords;
  }

  function getPredictiveImage(product) {
    if (!product) {
      return "";
    }

    if (product.featured_image && typeof product.featured_image === "object") {
      return product.featured_image.url || product.featured_image.src || "";
    }

    if (typeof product.featured_image === "string") {
      return product.featured_image;
    }

    if (product.image && typeof product.image === "object") {
      return product.image.url || product.image.src || "";
    }

    if (typeof product.image === "string") {
      return product.image;
    }

    return "";
  }

  async function getGiftProductFromConfig(gift) {
    const handle = gift?.freeProductHandle;

    if (!handle) {
      return null;
    }

    const cacheKey = `gift:${handle}:${gift.freeVariantId || ""}`;

    if (productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    const promise = (async () => {
      try {
        const response = await fetch(`/products/${handle}.js`, {
          credentials: "same-origin",

          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          return null;
        }

        const product = await response.json();

        const variants = product?.variants || [];

        let variant = null;

        if (gift.freeVariantId) {
          variant =
            variants.find(
              (item) => String(item.id) === String(gift.freeVariantId),
            ) || null;
        }

        if (!variant) {
          variant =
            variants.find((item) => item.available) || variants[0] || null;
        }

        const image =
          variant?.featured_image?.src ||
          variant?.featured_image?.url ||
          product?.featured_image ||
          "";

        const originalPriceCents =
          variant?.price != null ? Number(variant.price) : null;

        const isFree =
          String(gift.discountType || "").toUpperCase() === "FREE_PRODUCT";

        let finalPriceCents = originalPriceCents;

        if (isFree) {
          finalPriceCents = 0;
        }

        const discountValue = Number(gift.discountValue);

        if (
          !isFree &&
          Number.isFinite(originalPriceCents) &&
          Number.isFinite(discountValue) &&
          discountValue > 0
        ) {
          const discountType = String(gift.discountType || "").toUpperCase();

          if (
            discountType.includes("PERCENT") ||
            discountType === "PERCENTAGE"
          ) {
            finalPriceCents = originalPriceCents * (1 - discountValue / 100);
          }
        }

        return {
          title: product?.title || gift.freeProductTitle || "",

          image,

          url: `/products/${handle}`,

          comparePrice:
            originalPriceCents != null
              ? formatMoneyFromCents(originalPriceCents)
              : "",

          price:
            finalPriceCents != null
              ? formatMoneyFromCents(finalPriceCents)
              : "",

          gift: isFree,

          discountType: gift.discountType || "",
        };
      } catch (error) {
        console.warn("[AS Appstle] Gift load failed:", handle, error);

        return null;
      }
    })();

    productCache.set(cacheKey, promise);

    return promise;
  }

  async function searchShopifyProduct(reward) {
    const cacheKey = normalize(reward.rawTitle || reward.searchTitle);

    if (productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    const promise = (async () => {
      try {
        const query = encodeURIComponent(reward.searchTitle);

        const response = await fetch(
          `/search/suggest.json?q=${query}&resources[type]=product&resources[limit]=10`,
          {
            credentials: "same-origin",

            headers: {
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          return null;
        }

        const data = await response.json();

        const products = data?.resources?.results?.products || [];

        if (!products.length) {
          return null;
        }

        const sorted = [...products].sort(
          (a, b) => getProductScore(b, reward) - getProductScore(a, reward),
        );

        const predictiveProduct = sorted[0];

        if (!predictiveProduct) {
          return null;
        }

        const productUrl = String(predictiveProduct.url || "").split("?")[0];

        let fullProduct = null;

        if (productUrl) {
          const productResponse = await fetch(`${productUrl}.js`, {
            credentials: "same-origin",

            headers: {
              Accept: "application/json",
            },
          });

          if (productResponse.ok) {
            fullProduct = await productResponse.json();
          }
        }

        const variants = fullProduct?.variants || [];

        const variantHint = getRewardVariantHint(reward);

        let matchedVariant = null;

        if (variantHint && variants.length) {
          matchedVariant =
            variants.find((variant) => {
              const variantTitle = normalize(variant.title);

              return variantTitle === variantHint;
            }) ||
            variants.find((variant) => {
              const variantTitle = normalize(variant.title);

              return (
                variantTitle.includes(variantHint) ||
                variantHint.includes(variantTitle)
              );
            }) ||
            null;
        }

        if (!matchedVariant && variants.length) {
          matchedVariant =
            variants.find((variant) => variant.available) ||
            variants[0] ||
            null;
        }

        const realPrice =
          matchedVariant?.price != null
            ? formatMoneyFromCents(matchedVariant.price)
            : "";

        const variantImage =
          matchedVariant?.featured_image?.src ||
          matchedVariant?.featured_image?.url ||
          "";

        const productImage =
          fullProduct?.featured_image || getPredictiveImage(predictiveProduct);

        return {
          title:
            fullProduct?.title || predictiveProduct.title || reward.searchTitle,

          image: variantImage || productImage || "",

          url: productUrl || predictiveProduct.url || "",

          comparePrice: realPrice,

          price: "$0.00",

          gift: true,
        };
      } catch (error) {
        console.warn(
          "[AS Appstle] Gift product search failed:",
          reward.searchTitle,
          error,
        );

        return null;
      }
    })();

    productCache.set(cacheKey, promise);

    return promise;
  }

  async function getGiftProducts(plan) {
    const rewards = getAppstleRewards(plan);

    if (!rewards.length) {
      return [];
    }

    const products = await Promise.all(
      rewards.map(async (reward) => {
        const product = await searchShopifyProduct(reward);

        return (
          product || {
            title: reward.searchTitle || reward.rawTitle,

            image: "",

            url: "",

            comparePrice: reward.comparePrice,

            price: "$0.00",

            gift: true,
          }
        );
      }),
    );

    return products.filter(Boolean);
  }

  /* =========================================================
     PRODUCT CARDS
  ========================================================= */

  function createProducts(products) {
    if (!products.length) {
      return "";
    }

    return `
      <div class="as-appstle-products">

        ${products
          .map((product) => {
            const title = escapeHtml(product.title);

            const image = escapeHtml(product.image);

            const url = escapeHtml(product.url);

            const comparePrice = escapeHtml(product.comparePrice);

            const price = escapeHtml(product.price);

            const imageMarkup = image
              ? `
                  <img
                    class="as-appstle-product__image"
                    src="${image}"
                    alt="${title}"
                    loading="lazy"
                  >
                `
              : `
                  <div
                    class="as-appstle-product__image-placeholder"
                    aria-hidden="true"
                  ></div>
                `;

            const imageContent = `
              <div class="as-appstle-product__image-wrap">

                ${imageMarkup}

                ${
                  product.gift
                    ? `
                      <span class="as-appstle-product__gift">
                        ${giftIcon()}
                      </span>
                    `
                    : ""
                }

              </div>
            `;

            return `
              <div class="as-appstle-product">

                ${
                  url
                    ? `
                      <a
                        href="${url}"
                        class="as-appstle-product__image-link"
                      >
                        ${imageContent}
                      </a>
                    `
                    : imageContent
                }

                <div class="as-appstle-product__title">
                  ${title}
                </div>

                ${
                  comparePrice || price
                    ? `
                      <div class="as-appstle-product__prices">

                        ${
                          comparePrice && comparePrice !== price
                            ? `
                              <span class="as-appstle-product__compare">
                                ${comparePrice}
                              </span>
                            `
                            : ""
                        }

                        ${
                          price
                            ? `
                              <span class="as-appstle-product__price">
                                  ${price}
                                </span>
                            `
                            : ""
                        }

                      </div>
                    `
                    : ""
                }

              </div>
            `;
          })
          .join("")}

      </div>
    `;
  }

  /* =========================================================
     CUSTOM PLAN HEADER
  ========================================================= */

  function buildHead(plan, planData) {
    if (!planData) {
      return;
    }

    const discount = getPlanDiscount(plan);

    const price = findPlanPrice(plan, planData.type);

    const giftCount =
      planData.type === "subscription" && planData.months
        ? getPlanGiftCount(planData.months)
        : 0;

    const signature = [
      planData.type,
      planData.months || "",
      discount,
      price,
      giftCount,
    ].join("|");

    const existingHead = plan.querySelector(".as-appstle-plan-head");

    if (existingHead && plan.dataset.asHeadSignature === signature) {
      return;
    }

    if (existingHead) {
      existingHead.remove();
    }

    plan.dataset.asHeadSignature = signature;

    const head = document.createElement("div");

    head.className = "as-appstle-plan-head";

    head.innerHTML = `
      <span
        class="as-appstle-plan-radio"
        aria-hidden="true"
      ></span>

      <span class="as-appstle-plan-title">
        ${escapeHtml(getTitle(planData))}
      </span>

      ${
        discount
          ? `
            <span class="as-appstle-plan-badge">
              ${escapeHtml(discount)}
            </span>
          `
          : ""
      }

      ${
        giftCount
          ? `
            <span class="as-appstle-plan-gift-label">
              ${giftIcon()}

              <span class="as-appstle-plan-gift-count">
                +${giftCount}
              </span>

              <span class="as-appstle-plan-gift-text">
                ${giftCount === 1 ? "free gift" : "free gifts"}
              </span>
            </span>
          `
          : ""
      }

      ${
        price
          ? `
            <span class="as-appstle-plan-price">
              ${
                planData.type === "one-time"
                  ? escapeHtml(price)
                  : `
                      <span class="as-appstle-plan-price__amount">
                        ${escapeHtml(price.replace("/bottle", ""))}
                      </span>

                      <span class="as-appstle-plan-price__unit">
                        /bottle
                      </span>
                    `
              }
            </span>
          `
          : ""
      }
    `;

    plan.insertBefore(head, plan.firstChild);
  }

  /* =========================================================
     GIFT LABEL
  ========================================================= */

  function updateGiftLabel(plan, giftCount) {
    const label = plan.querySelector(".as-appstle-plan-gift-label");

    if (!label) {
      return;
    }

    if (!giftCount) {
      label.hidden = true;
      label.innerHTML = "";

      return;
    }

    label.hidden = false;

    label.innerHTML = `
      ${giftIcon()}

      <span>
        +${giftCount}
        ${giftCount === 1 ? "free gift" : "free gifts"}
      </span>
    `;
  }

  function getRewardSnapshot(widget) {
    if (!widget) {
      return "";
    }

    return Array.from(widget.querySelectorAll(REWARD_SELECTOR))
      .map((element) => (element.textContent || "").replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join("||");
  }

  function waitForLoyaltyToSettle(plan, maxWait = 1000) {
    const widget = plan.closest(WIDGET_SELECTOR);

    if (!widget) {
      return Promise.resolve();
    }

    const initialSnapshot = getRewardSnapshot(widget);

    return new Promise((resolve) => {
      let finished = false;
      let quietTimer = null;

      const finish = () => {
        if (finished) {
          return;
        }

        finished = true;

        clearTimeout(quietTimer);

        clearTimeout(maxTimer);

        observer.disconnect();

        resolve();
      };

      const scheduleFinish = () => {
        clearTimeout(quietTimer);

        quietTimer = setTimeout(() => {
          finish();
        }, 80);
      };

      const observer = new MutationObserver(() => {
        const currentSnapshot = getRewardSnapshot(widget);

        if (currentSnapshot !== initialSnapshot) {
          scheduleFinish();
        }
      });

      observer.observe(widget, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      const maxTimer = setTimeout(finish, maxWait);

      setTimeout(scheduleFinish, 100);
    });
  }

  /* =========================================================
     VARIANT CHANGE
  ========================================================= */

  document.addEventListener("change", (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const isVariantControl =
      target.matches('input[name="id"]') ||
      target.matches('select[name="id"]') ||
      Boolean(
        target.closest(
          "variant-selects, variant-radios, [data-variant-picker], .product-form__input",
        ),
      );

    if (!isVariantControl) {
      return;
    }

    setTimeout(() => {
      document
        .querySelectorAll(`${PLAN_SELECTOR}.${ACTIVE_CLASS}`)
        .forEach((plan) => {
          plan.dataset.asRewardSignature = "";

          refreshPlanGifts(plan);
        });
    }, 150);
  });

  /* =========================================================
     EXTRA CONTENT
  ========================================================= */

  async function refreshPlanGifts(plan) {
    const type = plan.dataset.asPlanType;

    const months = Number(plan.dataset.asPlanMonths || 0);

    if (type !== "subscription" || !months) {
      return;
    }

    const details = plan.querySelector(DETAILS_SELECTOR);

    if (!details) {
      return;
    }

    let extra = details.querySelector(".as-appstle-extra");

    if (!extra) {
      extra = document.createElement("div");
      extra.className = "as-appstle-extra";
      details.appendChild(extra);
    }

    extra.classList.add("is-loading");

    /*
     * Поточний variant і gifts вантажимо паралельно.
     */
    const currentProductPromise = getCurrentVariantProduct(plan);

    const giftDefinitions = getPlanGiftDefinitions(months);

    const giftsPromise = Promise.all(
      giftDefinitions.map((gift) => getGiftProductFromConfig(gift)),
    );

    const [currentProduct, gifts] = await Promise.all([
      currentProductPromise,
      giftsPromise,
    ]);

    /*
     * ГОЛОВНА ЗМІНА:
     *
     * currentProduct кладемо в ТОЙ САМИЙ масив,
     * що й gifts.
     *
     * Тому він проходить через createProducts()
     * і отримує ТОЧНО ТАКУ САМУ картку / image size.
     *
     * У currentProduct gift:false,
     * тому gift badge не показується.
     */
    const products = [currentProduct, ...gifts].filter(Boolean);

    extra.innerHTML = createProducts(products);

    extra.dataset.asLoaded = "true";
    extra.classList.remove("is-loading");
  }

  /* =========================================================
     ACCORDION
  ========================================================= */

  function setInitialState(plan) {
    const details = plan.querySelector(DETAILS_SELECTOR);

    if (!details) {
      return;
    }

    const isActive = plan.classList.contains(ACTIVE_CLASS);

    details.style.transition = "none";

    details.style.overflow = "hidden";

    if (isActive) {
      details.style.height = "auto";

      details.style.opacity = "1";
    } else {
      details.style.height = "0px";

      details.style.opacity = "0";
    }

    plan.dataset.asWasActive = isActive ? "true" : "false";

    requestAnimationFrame(() => {
      details.style.transition = "";
    });
  }

  function openPlan(plan) {
    const details = plan.querySelector(DETAILS_SELECTOR);

    if (!details) {
      return;
    }

    if (details._asTransitionEnd) {
      details.removeEventListener("transitionend", details._asTransitionEnd);
    }

    details.dataset.asAnimating = "true";

    details.style.overflow = "hidden";

    details.style.height = "0px";

    details.style.opacity = "0";

    details.getBoundingClientRect();

    const targetHeight = details.scrollHeight;

    details.style.height = `${targetHeight}px`;

    details.style.opacity = "1";

    const onTransitionEnd = (event) => {
      if (event.propertyName !== "height") {
        return;
      }

      details.removeEventListener("transitionend", onTransitionEnd);

      details._asTransitionEnd = null;

      if (!plan.classList.contains(ACTIVE_CLASS)) {
        return;
      }

      delete details.dataset.asAnimating;

      details.style.height = "auto";

      details.style.opacity = "1";

      details.style.overflow = "hidden";
    };

    details._asTransitionEnd = onTransitionEnd;

    details.addEventListener("transitionend", onTransitionEnd);
  }

  function closePlan(plan) {
    const details = plan.querySelector(DETAILS_SELECTOR);

    if (!details) {
      return;
    }

    if (details._asTransitionEnd) {
      details.removeEventListener("transitionend", details._asTransitionEnd);

      details._asTransitionEnd = null;
    }

    details.dataset.asAnimating = "true";

    const currentHeight = details.getBoundingClientRect().height;

    details.style.overflow = "hidden";

    details.style.height = `${currentHeight}px`;

    details.style.opacity = "1";

    details.getBoundingClientRect();

    details.style.height = "0px";

    details.style.opacity = "0";

    const onTransitionEnd = (event) => {
      if (event.propertyName !== "height") {
        return;
      }

      details.removeEventListener("transitionend", onTransitionEnd);

      details._asTransitionEnd = null;

      delete details.dataset.asAnimating;
    };

    details._asTransitionEnd = onTransitionEnd;

    details.addEventListener("transitionend", onTransitionEnd);
  }

  function handlePlanState(plan) {
    const isActive = plan.classList.contains(ACTIVE_CLASS);

    const wasActive = plan.dataset.asWasActive === "true";

    if (isActive === wasActive) {
      return;
    }

    plan.dataset.asWasActive = isActive ? "true" : "false";

    if (!isActive) {
      closePlan(plan);

      return;
    }

    openPlan(plan);

    plan.dataset.asRewardSignature = "";

    setTimeout(() => {
      if (plan.classList.contains(ACTIVE_CLASS)) {
        refreshPlanGifts(plan);
      }
    }, 180);
  }

  /* =========================================================
     PLAN INITIALISATION
  ========================================================= */

  function selectAppstlePlan(plan) {
    if (!plan) {
      return;
    }

    if (plan.dataset.asSelecting === "true") {
      return;
    }

    const input = plan.querySelector(
      'input[type="radio"][name="selling_plan"]',
    );

    const label = input?.id
      ? plan.querySelector(`label[for="${CSS.escape(input.id)}"]`)
      : plan.querySelector("label");

    if (!input) {
      return;
    }

    plan.dataset.asSelecting = "true";

    if (label) {
      label.click();
    } else {
      input.click();
    }

    requestAnimationFrame(() => {
      delete plan.dataset.asSelecting;
    });
  }

  function syncPlanState(plan) {
    const details = plan.querySelector(DETAILS_SELECTOR);

    if (!details) {
      return;
    }

    if (details.dataset.asAnimating === "true") {
      return;
    }

    const isActive = plan.classList.contains(ACTIVE_CLASS);

    if (!details.dataset.asStateInitialized) {
      details.dataset.asStateInitialized = "true";

      details.style.overflow = "hidden";

      if (isActive) {
        details.style.height = "auto";

        details.style.opacity = "1";
      } else {
        details.style.height = "0px";

        details.style.opacity = "0";
      }
    }

    plan.dataset.asWasActive = isActive ? "true" : "false";
  }

  function getStoredPlanData(plan) {
    const type = plan.dataset.asPlanType;

    if (!type) {
      return null;
    }

    if (type === "one-time") {
      return {
        type: "one-time",
        months: null,
      };
    }

    const months = Number(plan.dataset.asPlanMonths || 0);

    if (type === "subscription" && months) {
      return {
        type: "subscription",
        months,
      };
    }

    return null;
  }

  function applyPlanMetadata(plan, planData) {
    plan.dataset.asPlanType = planData.type;

    plan.classList.remove(
      "as-appstle-plan--one-time",
      "as-appstle-plan--subscription",
    );

    Array.from(plan.classList)
      .filter((className) => /^as-appstle-plan--\d+-month$/.test(className))
      .forEach((className) => plan.classList.remove(className));

    if (planData.type === "one-time") {
      delete plan.dataset.asPlanMonths;

      plan.classList.add("as-appstle-plan--one-time");

      return;
    }

    plan.dataset.asPlanMonths = String(planData.months);

    plan.classList.add(
      "as-appstle-plan--subscription",
      `as-appstle-plan--${planData.months}-month`,
    );
  }

  function setupPlan(plan) {
    const detectedPlanData = getPlanType(plan);

    const planData = detectedPlanData || getStoredPlanData(plan);

    if (!planData) {
      return;
    }

    applyPlanMetadata(plan, planData);

    buildHead(plan, planData);

    if (plan.dataset.asPlanInitialized === "true") {
      const details = plan.querySelector(DETAILS_SELECTOR);

      if (
        details &&
        !details.dataset.asStateInitialized &&
        details.dataset.asAnimating !== "true"
      ) {
        syncPlanState(plan);
      }

      if (
        plan.classList.contains(ACTIVE_CLASS) &&
        details &&
        details.dataset.asAnimating !== "true"
      ) {
        refreshPlanGifts(plan);
      }

      return;
    }

    plan.dataset.asPlanInitialized = "true";

    if (plan.dataset.asCardClickInit !== "true") {
      plan.dataset.asCardClickInit = "true";

      plan.addEventListener("click", (event) => {
        if (plan.dataset.asSelecting === "true") {
          return;
        }

        if (event.target.closest(".as-appstle-product__image-link")) {
          return;
        }

        if (plan.classList.contains(ACTIVE_CLASS)) {
          return;
        }

        event.preventDefault();

        selectAppstlePlan(plan);
      });
    }

    setInitialState(plan);

    syncPlanState(plan);

    if (plan.classList.contains(ACTIVE_CLASS)) {
      refreshPlanGifts(plan);
    }

    const stateObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          handlePlanState(plan);
        }
      }
    });

    stateObserver.observe(plan, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  /* =========================================================
     WIDGET STRUCTURE OBSERVER
  ========================================================= */

  function setupWidget(widget) {
    widget.querySelectorAll(PLAN_SELECTOR).forEach(setupPlan);

    if (widget.dataset.asWidgetObserver === "true") {
      return;
    }

    widget.dataset.asWidgetObserver = "true";

    let scheduled = false;

    const structureObserver = new MutationObserver(() => {
      if (scheduled) {
        return;
      }

      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;

        widget.querySelectorAll(PLAN_SELECTOR).forEach((plan) => {
          setupPlan(plan);

          if (
            plan.classList.contains(ACTIVE_CLASS) &&
            !plan.querySelector(DETAILS_SELECTOR)?.dataset.asAnimating
          ) {
            refreshPlanGifts(plan);
          }
        });
      });
    });

    structureObserver.observe(widget, {
      childList: true,
      subtree: true,
    });
  }

  /* =========================================================
     PRELOAD GIFT PRODUCTS
  ========================================================= */

  function getAvailableSubscriptionMonths() {
    const plans = window.AppstleSubscription?.Config?.sellingPlansJson || [];

    return [
      ...new Set(
        plans
          .map((plan) =>
            getPlanMonthsFromValue(plan.frequencyName || plan.name || ""),
          )
          .filter(Boolean),
      ),
    ];
  }

  function preloadGiftProducts() {
    getAvailableSubscriptionMonths().forEach((months) => {
      const gifts = getPlanGiftDefinitions(months);

      gifts.forEach((gift) => {
        getGiftProductFromConfig(gift);
      });
    });
  }

  function init() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(setupWidget);
  }

  function watchAppstleWidgets() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(setupWidget);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches?.(WIDGET_SELECTOR)) {
            setupWidget(node);
          }

          node.querySelectorAll?.(WIDGET_SELECTOR).forEach(setupWidget);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  watchAppstleWidgets();

  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => {
      preloadGiftProducts();
    });
  } else {
    setTimeout(() => {
      preloadGiftProducts();
    }, 300);
  }
})();
